import { useEffect, useRef, useState } from "react";
import { format } from "date-fns";
import { Loader2, Send } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const Bubble = ({ m, mine }) => (
  <li data-testid={`message-${m.id}`} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
    <div className={`max-w-[85%] rounded-2xl px-4 py-3 sm:max-w-[70%] ${mine ? "rounded-br-sm border border-cyan-400/25 bg-cyan-400/10" : "rounded-bl-sm border border-white/10 bg-[#111827]"}`}>
      <p className="mb-1 text-xs">
        <span data-testid={`message-sender-${m.id}`} className={mine ? "font-semibold text-cyan-300" : "font-semibold text-slate-200"}>{m.sender_name}</span>
        <span data-testid={`message-time-${m.id}`} className="ml-2 text-slate-500">{format(new Date(m.created_at), "MMM d, h:mm a")}</span>
      </p>
      <p data-testid={`message-body-${m.id}`} className="whitespace-pre-wrap break-words text-sm text-slate-100">{m.body}</p>
    </div>
  </li>
);

export const MessagesPanel = ({ projectId }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const end = useRef(null);

  const load = () => api.get(`/projects/${projectId}/messages`).then((r) => setMessages(r.data)).catch(() => setMessages((m) => m || []));
  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { end.current?.scrollIntoView({ block: "nearest" }); }, [messages?.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/projects/${projectId}/messages`, { body: text });
      setMessages((ms) => [...(ms || []), data]);
      setText("");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="messages-panel" className="rounded-2xl border border-white/5 bg-[#0B0F18]">
      <div className="max-h-[480px] overflow-y-auto p-4 sm:p-6">
        {messages === null ? <div className="h-24 animate-pulse rounded-xl bg-white/[0.03]" /> : messages.length === 0 ? (
          <p data-testid="messages-empty-state" className="py-10 text-center text-sm text-slate-500">No messages yet. Start the conversation below.</p>
        ) : (
          <ul data-testid="messages-thread" className="space-y-4">
            {messages.map((m) => <Bubble key={m.id} m={m} mine={m.sender_id === user.id} />)}
          </ul>
        )}
        <div ref={end} />
      </div>
      <form onSubmit={send} className="flex flex-col gap-3 border-t border-white/5 p-4 sm:flex-row sm:items-end">
        <textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Write a message..." data-testid="message-input"
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(e); }}
          className="flex-1 resize-none rounded-lg border border-white/10 bg-[#07090E] px-3 py-2.5 text-sm text-white outline-none focus:border-cyan-400/60" />
        <button type="submit" disabled={!text.trim() || busy} data-testid="message-send-button"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-cyan-400 px-5 py-2.5 text-sm font-semibold text-[#07090E] transition-opacity hover:bg-cyan-300 disabled:cursor-not-allowed disabled:opacity-40">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send
        </button>
      </form>
      {error && <p data-testid="message-error" className="px-4 pb-4 text-sm text-red-400">{error}</p>}
    </div>
  );
};
